import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addUpvoteToReview,
  removeUpvoteFromReview,
} from 'flux/clients/reviews';
import { useHistory } from 'react-router-dom';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import { useI18n } from '~/stores/i18n';

const useUpvoteReview = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { lang } = useI18n();

  const handleUnauthorizedError = () => {
    const searchParams = new URLSearchParams();
    searchParams.set('returnTo', window.location.href);
    history.push({
      pathname: generatePathnameWithLangCode(`/login/`, lang),
      search: searchParams.toString(),
      state: { modal: true },
    });
  };

  const addMutation = useMutation({
    mutationFn: (reviewId: string) => addUpvoteToReview(reviewId),
    onError: (error: any) => {
      if (error.status === 401) {
        handleUnauthorizedError();
      } else {
        console.error('Error upvoting review', error);
      }
    },
    onSuccess: (_, reviewId) => {
      queryClient.setQueryData(
        ['experience-review', reviewId],
        (oldData: any) => ({
          ...oldData,
          votesUp: oldData.votesUp + 1,
          votedByCurrentUser: true,
        }),
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (reviewId: string) => removeUpvoteFromReview(reviewId),
    onError: (error: any) => {
      if (error.status === 401) {
        handleUnauthorizedError();
      } else {
        console.error('Error un-upvoting review', error);
      }
    },
    onSuccess: (_, reviewId) => {
      queryClient.setQueryData(
        ['experience-review', reviewId],
        (oldData: any) => ({
          ...oldData,
          votesUp: Math.max(oldData.votesUp - 1, 0),
          votedByCurrentUser: false,
        }),
      );
    },
  });

  return {
    addMutation,
    removeMutation,
    isLoading: addMutation.isLoading || removeMutation.isLoading,
  };
};

export default useUpvoteReview;
